import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../App';
import { projectsAPI, bomAPI, estimatesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import appLogo from '../assets/logo.png';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import NotificationBell from './NotificationBell';
import {
  LogOut,
  FolderKanban,
  Layers,
  Clock,
  CheckCircle,
  Scissors,
  CircleDot,
  Package,
  Image,
  Eye,
  Receipt,
  Download,
  FileSpreadsheet,
  IndianRupee
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const STORAGE_KEY = 'client_materials_last_seen';

// Client-friendly BOM status labels
const getBOMStatusLabel = (status) => {
  switch (status) {
    case 'sent_to_client':
      return '📋 NEW REQUEST';
    case 'received':
      return '✅ MATERIALS RECEIVED';
    case 'draft':
      return '📝 DRAFT';
    default:
      return status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN';
  }
};

const getLastSeen = (userId) => {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return data[userId] ? new Date(data[userId]) : null;
  } catch { return null; }
};

const setLastSeen = (userId) => {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    data[userId] = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

const getBomLatestTimestamp = (bom) => {
  const timestamps = [
    bom.invoice_uploaded_at,
    bom.received_at,
    bom.sent_at,
    bom.created_at
  ].filter(Boolean).map(t => new Date(t));
  return timestamps.length > 0 ? new Date(Math.max(...timestamps)) : null;
};

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [boms, setBoms] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [viewingPhotoUrl, setViewingPhotoUrl] = useState(null);
  const [selectedBOM, setSelectedBOM] = useState(null);
  const [showBOMDetail, setShowBOMDetail] = useState(false);
  const [lastSeenTime, setLastSeenTime] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);

  useEffect(() => {
    fetchData();
    if (user?.id) setLastSeenTime(getLastSeen(user.id));
  }, []);

  const fetchData = async () => {
    try {
      const [projectsData, bomsData, invoicesData] = await Promise.all([
        projectsAPI.getAll(),
        bomAPI.getAll().catch(() => []),
        estimatesAPI.getClientInvoices().catch(() => [])
      ]);
      setProjects(projectsData);
      setBoms(bomsData);
      setInvoices(invoicesData);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const isBomNew = useCallback((bom) => {
    if (!lastSeenTime) return true;
    const bomTime = getBomLatestTimestamp(bom);
    return bomTime && bomTime > lastSeenTime;
  }, [lastSeenTime]);

  const newBomCount = useMemo(() => boms.filter(isBomNew).length, [boms, isBomNew]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'materials' && user?.id) {
      setLastSeen(user.id);
    }
  };

  const handleDownloadInvoicePDF = async (est) => {
    try {
      const blob = await estimatesAPI.exportPDF(est.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${est.estimate_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success", description: "Invoice PDF downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to download PDF", variant: "destructive" });
    }
  };

  const handleDownloadInvoiceExcel = async (est) => {
    try {
      const blob = await estimatesAPI.exportExcel(est.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${est.estimate_number}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success", description: "Invoice Excel downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to download Excel", variant: "destructive" });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_cutting': return <Scissors className="w-5 h-5" />;
      case 'in_pressing': return <Layers className="w-5 h-5" />;
      case 'in_boring': return <CircleDot className="w-5 h-5" />;
      case 'finishing': return <Package className="w-5 h-5" />;
      case 'completed':
      case 'invoiced': return <CheckCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500';
      case 'in_cutting': return 'bg-yellow-500';
      case 'in_pressing': return 'bg-orange-500';
      case 'in_boring': return 'bg-purple-500';
      case 'finishing': return 'bg-indigo-500';
      case 'completed': return 'bg-green-500';
      case 'invoiced': return 'bg-emerald-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_cutting': return 'bg-yellow-100 text-yellow-800';
      case 'in_pressing': return 'bg-orange-100 text-orange-800';
      case 'in_boring': return 'bg-purple-100 text-purple-800';
      case 'finishing': return 'bg-indigo-100 text-indigo-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'invoiced': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
              <img src={appLogo} alt="Interior Job Work" className="w-[60px] h-[60px] object-cover" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Interior Job Work</h1>
              <p className="text-xs text-gray-500">Track your orders</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 hidden sm:block">{user?.name}</span>
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{projects.length}</p>
              <p className="text-sm text-gray-500">Total Job Cards</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">
                {projects.filter(p => !['completed', 'invoiced'].includes(p.status)).length}
              </p>
              <p className="text-sm text-gray-500">In Progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">
                {projects.filter(p => ['completed', 'invoiced'].includes(p.status)).length}
              </p>
              <p className="text-sm text-gray-500">Completed</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="grid grid-cols-3 w-full max-w-[400px]">
            <TabsTrigger value="projects" className="flex items-center gap-2" data-testid="client-projects-tab">
              <FolderKanban className="w-4 h-4" />
              Job Cards
            </TabsTrigger>
            <TabsTrigger value="materials" className="flex items-center gap-2 relative" data-testid="client-materials-tab">
              <Package className="w-4 h-4" />
              Materials
              {newBomCount > 0 && (
                <span className="ml-1 min-w-5 h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="materials-notification-badge">
                  {newBomCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="client-invoices-tab">
              <Receipt className="w-4 h-4" />
              Invoices
              {invoices.length > 0 && (
                <span className="ml-1 min-w-5 h-5 px-1 bg-green-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {invoices.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Job Cards Tab */}
          <TabsContent value="projects" className="space-y-4" data-testid="client-projects-content">
            {projects.length > 0 ? (
              projects.map(project => (
                <Card key={project.id} className="overflow-hidden">
                  <div className={`h-1 ${getStatusColor(project.status)}`}></div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{project.job_card_number || project.project_number}</h3>
                        {project.reference && (
                          <p className="text-sm text-gray-500">Ref: {project.reference}</p>
                        )}
                        {project.description && (
                          <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                        )}
                      </div>
                      <Badge className={getStatusBadgeColor(project.status)}>
                        {formatStatus(project.status)}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Progress</span>
                        <span className="font-medium">{project.completion_percentage}%</span>
                      </div>
                      <Progress value={project.completion_percentage} className="h-2" />
                    </div>

                    <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                      <div className={`p-2 rounded-full ${getStatusBadgeColor(project.status)}`}>
                        {getStatusIcon(project.status)}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Current Stage</p>
                        <p className="font-medium">{formatStatus(project.status)}</p>
                      </div>
                    </div>

                    <div className="mt-3 text-xs text-gray-500">
                      Created: {new Date(project.created_date).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <FolderKanban className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Job Cards Yet</h3>
                  <p className="text-gray-500">Your projects will appear here once created.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Materials / BOM Tab */}
          <TabsContent value="materials" className="space-y-4" data-testid="client-materials-content">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" />
              Bill of Materials
            </h2>

            {boms.length > 0 ? (
              boms.map(bom => {
                const isNew = isBomNew(bom);
                const hasNewInvoice = bom.invoice_uploaded_at && lastSeenTime && new Date(bom.invoice_uploaded_at) > lastSeenTime;
                return (
                <Card key={bom.id} className={`${isNew ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`} data-testid={`client-bom-card-${bom.bom_number}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold">{bom.bom_number}</h3>
                          <Badge className={
                            bom.status === 'received' ? 'bg-green-100 text-green-800' :
                            bom.status === 'sent_to_client' ? 'bg-amber-100 text-amber-800 font-semibold' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {getBOMStatusLabel(bom.status)}
                          </Badge>
                          {bom.invoice_photo_url && (
                            <Badge className="bg-purple-100 text-purple-800">Invoice Available</Badge>
                          )}
                          {isNew && (
                            <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0" data-testid={`bom-new-badge-${bom.bom_number}`}>NEW</Badge>
                          )}
                          {hasNewInvoice && !isNew && (
                            <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0" data-testid={`bom-new-invoice-badge-${bom.bom_number}`}>NEW INVOICE</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{bom.materials?.length || 0} materials</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => { setSelectedBOM(bom); setShowBOMDetail(true); }}
                          data-testid={`client-view-bom-${bom.bom_number}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        {bom.invoice_photo_url && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setViewingPhotoUrl(bom.invoice_photo_url); setShowPhotoDialog(true); }}
                            data-testid={`client-view-invoice-${bom.bom_number}`}
                          >
                            <Image className="w-4 h-4 mr-1" /> Invoice
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Material summary */}
                    {bom.materials && bom.materials.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex flex-wrap gap-2">
                          {bom.materials.slice(0, 3).map((mat, i) => (
                            <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {mat.material} ({mat.quantity} {mat.unit})
                            </span>
                          ))}
                          {bom.materials.length > 3 && (
                            <span className="text-xs text-gray-500 px-2 py-1">
                              +{bom.materials.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No materials lists yet</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4" data-testid="client-invoices-content">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Invoices
            </h2>

            {invoices.length > 0 ? (
              invoices.map(inv => (
                <Card key={inv.id} data-testid={`client-invoice-card-${inv.estimate_number}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold">{inv.estimate_number}</h3>
                          {inv.payment_received ? (
                            <Badge className="bg-green-500 text-white">Paid</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800">Invoice</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{inv.customer_name}</p>
                        {inv.job_card_number && (
                          <p className="text-xs text-gray-500">Job Card: {inv.job_card_number}</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Date: {new Date(inv.date).toLocaleDateString('en-IN')}
                        </p>
                        {inv.sent_to_client_at && (
                          <p className="text-xs text-purple-600">
                            Received: {new Date(inv.sent_to_client_at).toLocaleDateString('en-IN')}
                          </p>
                        )}
                        {inv.payment_received && inv.payment_date && (
                          <p className="text-xs text-green-600">
                            Paid: {new Date(inv.payment_date).toLocaleDateString('en-IN')}
                          </p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl font-bold text-green-600">
                          <IndianRupee className="w-4 h-4 inline" /> {(inv.total_amount || 0).toFixed(2)}
                        </p>
                        <div className="flex gap-2 mt-2 justify-end">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => { setViewingInvoice(inv); setShowInvoiceDetail(true); }}
                            data-testid={`client-view-invoice-btn-${inv.estimate_number}`}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDownloadInvoicePDF(inv)}
                            data-testid={`client-download-invoice-${inv.estimate_number}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Line items summary */}
                    {inv.estimate_lines && inv.estimate_lines.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex flex-wrap gap-2">
                          {inv.estimate_lines.slice(0, 3).map((line, i) => (
                            <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {String(line.particular || '').toUpperCase()} ({line.qty})
                            </span>
                          ))}
                          {inv.estimate_lines.length > 3 && (
                            <span className="text-xs text-gray-500 px-2 py-1">
                              +{inv.estimate_lines.length - 3} more items
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No invoices yet</p>
                  <p className="text-xs text-gray-400 mt-1">Invoices will appear here once sent by the accountant</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* BOM Detail Dialog */}
      <Dialog open={showBOMDetail} onOpenChange={setShowBOMDetail}>
        <DialogContent className="sm:max-w-2xl" data-testid="client-bom-detail-dialog">
          <DialogHeader>
            <DialogTitle>Materials - {selectedBOM?.bom_number}</DialogTitle>
          </DialogHeader>
          {selectedBOM && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={
                    selectedBOM.status === 'received' ? 'bg-green-100 text-green-800' :
                    selectedBOM.status === 'sent_to_client' ? 'bg-amber-100 text-amber-800 font-semibold' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {getBOMStatusLabel(selectedBOM.status)}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Items</p>
                  <p className="font-medium">{selectedBOM.materials?.length || 0} materials</p>
                </div>
              </div>

              <table className="w-full text-sm border">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-left w-10">#</th>
                    <th className="border p-2 text-left">Material</th>
                    <th className="border p-2 text-right w-24">Quantity</th>
                    <th className="border p-2 text-center w-20">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBOM.materials?.map((mat, idx) => (
                    <tr key={mat.id || idx}>
                      <td className="border p-2 text-gray-500">{idx + 1}</td>
                      <td className="border p-2 font-medium">{mat.material}</td>
                      <td className="border p-2 text-right">{mat.quantity}</td>
                      <td className="border p-2 text-center">{mat.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Invoice photo in BOM detail */}
              {selectedBOM.invoice_photo_url && (
                <div className="p-4 bg-blue-50 rounded-lg" data-testid="client-bom-invoice-section">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Image className="w-4 h-4 text-blue-600" />
                    Invoice Photo
                  </h4>
                  <img 
                    src={`${API_BASE}${selectedBOM.invoice_photo_url}`}
                    alt="Invoice" 
                    className="max-h-48 rounded-lg border cursor-pointer hover:opacity-90"
                    onClick={() => { setViewingPhotoUrl(selectedBOM.invoice_photo_url); setShowPhotoDialog(true); }}
                    data-testid="client-bom-invoice-photo"
                  />
                  {selectedBOM.invoice_uploaded_by && (
                    <p className="text-xs text-gray-500 mt-2">
                      Uploaded by: {selectedBOM.invoice_uploaded_by}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBOMDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Photo Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="client-view-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Invoice Photo</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2">
            {viewingPhotoUrl && (
              <img 
                src={`${API_BASE}${viewingPhotoUrl}`} 
                alt="Invoice" 
                className="max-h-[60vh] w-auto rounded-lg object-contain"
                data-testid="client-invoice-photo-image"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotoDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Detail Dialog */}
      <Dialog open={showInvoiceDetail} onOpenChange={setShowInvoiceDetail}>
        <DialogContent className="sm:max-w-2xl" data-testid="client-invoice-detail-dialog">
          <DialogHeader>
            <DialogTitle>Invoice - {viewingInvoice?.estimate_number}</DialogTitle>
          </DialogHeader>
          {viewingInvoice && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-xs text-gray-500">Customer</p>
                  <p className="font-medium">{viewingInvoice.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Date</p>
                  <p className="font-medium">{new Date(viewingInvoice.date).toLocaleDateString('en-IN')}</p>
                </div>
                {viewingInvoice.job_card_number && (
                  <div>
                    <p className="text-xs text-gray-500">Job Card</p>
                    <p className="font-medium">{viewingInvoice.job_card_number}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  {viewingInvoice.payment_received ? (
                    <Badge className="bg-green-500 text-white">Paid</Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-800">Pending Payment</Badge>
                  )}
                </div>
              </div>

              <table className="w-full text-sm border">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-center w-12">SL NO</th>
                    <th className="border p-2 text-left">PARTICULARS</th>
                    <th className="border p-2 text-right w-16">QTY</th>
                    <th className="border p-2 text-center w-16">UNIT</th>
                    <th className="border p-2 text-right w-20">RATE</th>
                    <th className="border p-2 text-right w-24">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingInvoice.estimate_lines.map(line => (
                    <tr key={line.id}>
                      <td className="border p-2 text-center">{line.sl_no}</td>
                      <td className="border p-2">{String(line.particular || '').toUpperCase()}</td>
                      <td className="border p-2 text-right">{line.qty}</td>
                      <td className="border p-2 text-center">{line.unit || ''}</td>
                      <td className="border p-2 text-right">{line.rate.toFixed(2)}</td>
                      <td className="border p-2 text-right font-medium">{line.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="bg-yellow-50 p-4 rounded border-2">
                <div className="flex justify-between py-2 text-lg font-bold">
                  <span>Total Amount:</span>
                  <span className="text-green-600">₹ {(viewingInvoice.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDetail(false)}>Close</Button>
            <Button onClick={() => handleDownloadInvoicePDF(viewingInvoice)}>
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
